import React, { createContext, useContext, useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    createColumnHelper,
    useReactTable,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
} from '@tanstack/react-table';
import { updateExtDataCategoriesOrder } from '../../store/extDataCategories/actions';
import constants from '../../commonUtils/constants';
import { useNavigate } from 'react-router-dom';
import { initiateDraftSession } from '../../store/extVersions/actions';

const ExtOutputMappingContext = createContext();

export const useExtOutputMapping = () => useContext(ExtOutputMappingContext);

export const ExtOutputMappingProvider = ({ children }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { extractionData } = useSelector((state) => state.Extractions);
    const { extVersion } = useSelector((state) => state.ExtVersions);
    const { ExtOutputMappings, loading, errors } = useSelector((state) => state.ExtOutputMappings);

    const [perPage, setPerPage] = useState(constants.tableUI.defaultPerpage);
    const [localTableData, setLocalTableData] = useState([]);
    const isReordering = useRef(false); // Track if we're currently reordering

    // Only provide helpers, do not auto-fetch mappings here

    const updateDataStrategy = useCallback(() => {
        // Set expiration 10 years into the future
        dispatch(initiateDraftSession({
            extId: extractionData?.id || extractionData?._id,
            extKey: extractionData?.key,
            versionId: extVersion?.id || extVersion?._id,
            navigate,
        }));
    }, [dispatch, extractionData, extVersion, navigate]);

    // Transform formatted output into table data
    const tableData = useMemo(() => {
        if (!ExtOutputMappings?.data) return [];

        return ExtOutputMappings.data.map((item, index) => ({
            id: index + 1,
            ...item,
        }));
    }, [ExtOutputMappings]);

    // DON'T update local data automatically - let the page handle it
    // Only clear localTableData when new data comes from API (not during reorder)
    useEffect(() => {
        if (!isReordering.current) {
            setLocalTableData([]);
        }
    }, [ExtOutputMappings?.data]);

    // Use local data if available (for reordering), otherwise use original data
    const displayData = localTableData.length > 0 ? localTableData : tableData;

    const pagination = useMemo(() => ExtOutputMappings?.pagination, [ExtOutputMappings?.pagination]);
    
    const handlePageChange = useCallback((currentPage, newPerPage) => {
        // Update perPage state if it changed
        if (newPerPage !== perPage) {
            setPerPage(newPerPage);
        }
        isReordering.current = false; // Reset on page change
        setLocalTableData([]); // Clear local data
        // The actual data fetch should be triggered by the page component
    }, [perPage]);

    // Save order function for the page component
    const saveOrder = useCallback((orderData) => {
        if (orderData.length > 0) {
            dispatch(updateExtDataCategoriesOrder(orderData));
        }
    }, [dispatch]);

    // Handle row reorder - now just updates local state, page handles save
    const handleRowReorder = useCallback((reorderedData) => {
        // Mark that we're reordering
        isReordering.current = true;
        
        // Update local state immediately for UI responsiveness
        setLocalTableData(reorderedData);
        
        // Keep the flag set for a bit to prevent immediate override
        setTimeout(() => {
            isReordering.current = false;
        }, 1000);
    }, []);

    // Function to get unique ID for each row
    const getRowId = useCallback((row) => {
        return row.id?.toString() || row._id?.toString() || '';
    }, []);

    // Reset local data when needed
    const resetOrder = useCallback(() => {
        isReordering.current = false;
        setLocalTableData([]);
    }, []);

    // Get column headers (data groups)
    const dataGroupHeaders = useMemo(() => {
        if (!ExtOutputMappings?.formatted) return [];

        const allGroups = new Set();
        Object.values(ExtOutputMappings.formatted).forEach(categoryData => {
            Object.keys(categoryData).forEach(group => allGroups.add(group));
        });

        return Array.from(allGroups);
    }, [ExtOutputMappings]);

    const columnHelper = createColumnHelper();
    const columns = useMemo(() => {
        const baseColumns = [
            columnHelper.accessor('id', { header: '#', cell: info => info.getValue() }),
            columnHelper.accessor('data_category', { header: 'Data Category', cell: info => info.getValue() }),
        ];

        // Add dynamic columns for each data group
        const groupColumns = dataGroupHeaders.map(group => 
            columnHelper.accessor(group, { 
                header: group, 
                cell: info => info.getValue() || 0 
            })
        );

        return [...baseColumns, ...groupColumns];
    }, [dataGroupHeaders, columnHelper]);

    const table = useReactTable({
        data: displayData,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getRowId: getRowId,
        state: {
            pagination: {
                pageIndex: (ExtOutputMappings?.pagination?.currentPage || 1) - 1,
                pageSize: perPage,
            },
        },
        manualPagination: true,
        pageCount: ExtOutputMappings?.pagination?.totalPages || 0,
    });

    return (
        <ExtOutputMappingContext.Provider value={{
            tableData: displayData,
            table,
            columns,
            dataGroupHeaders,
            loading,
            errors,
            pagination,
            handlePageChange,
            updateDataStrategy,
            handleRowReorder,
            getRowId,
            resetOrder,
            saveOrder, // Added this so page can call it
            formattedOutput: ExtOutputMappings?.formatted || {},
            rawMappings: ExtOutputMappings?.raw || [],
            totalMappings: ExtOutputMappings?.total || 0,
        }}>
            {children}
        </ExtOutputMappingContext.Provider>
    );
};